import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type Facility from '../../shared/types/Facility';
import type { TrialLensOrderParams, TrialOrderClItems, LensProduct, Stockout, TrialOrderClItem } from '../types';
import getTrialLens from '../api/getTrialLens';
import setLensDataInfo from '../api/setLensDataInfo';
import { useOrderContext } from '@/providers/OrderProvider';
import confirmTrialLens from '../api/confirmTrialLens';
import { toUnderscoreCase } from '@/utils/format';
import { SelectInputBox } from '@/components/SelectInput';
import { InputFieldBox } from '@/components/InputField';
import SidebarNavigation from '@/components/SidebarNavigation';
import AlertMessages from '@/components/AlertMessages';
import InfoDisplay from '@/components/InfoDisplay';
import Layout from '../../shared/components/Layout';
import TrialLensDataForm from '../components/TrialLensDataForm';
import clearLocalStorage from '@/utils/clearLocalStorage';

const  TrialLens: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { arrivalTimeZoneLabels, getProductOptions } = useOrderContext();

  const [facility, setFacility] = useState<Facility>();
  const [products, setProducts] = useState<LensProduct[]>([]);

  const [note, setNote] = useState<string>('');
  const [arrivalDate, setArrivalDate] = useState<string>('');
  const [arrivalTimeZone, setArrivalTimeZone] = useState<string>('no_preference');
  const [expectedShipDate, setExpectedShipDate] = useState<string>('');
  const [shortestArrivalDate, setShortestArrivalDate] = useState<string>('');

  const [lenses, setLenses] = useState<TrialOrderClItems>({});

  const [productName, setProductName] = useState<string>()
  const [isAstigma, setIsAstigma] = useState<boolean>(false);
  const [productId, setProductId] = useState<number>();

  const [alertMessageOpen, setAlertMessageOpen] = useState<boolean>(false);
  const [alertMessages, setAlertMessages] = useState<string[]>([]);
  const [arrivalDates, setArrivalDates] = useState<string[]>([]);

  const [initialExpectedShipDate, setInitialExpectedShipDate] = useState<string>('');
  const [initialShortestArrivalDate, setInitialShortestArrivalDate] = useState<string>('');
  const [initialArrivalDates, setInitialArrivalDates] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getTrialLens();

        setFacility(response.data.facility);
        setProducts(response.data.products);

        setExpectedShipDate(response.data.expectedShipDate);
        setInitialExpectedShipDate(response.data.expectedShipDate);

        setShortestArrivalDate(response.data.shortestArrivalDate);
        setInitialShortestArrivalDate(response.data.shortestArrivalDate);

        setArrivalDates(response.data.arrivalDates);
        setInitialArrivalDates(response.data.arrivalDates);
      } catch (error) {
        console.error("Error fetching commercial lens data:", error);
      }
    };

    fetchData();

    const fromConfirmPage = location.state?.fromConfirmPage;
    if(fromConfirmPage) {
      const storedOrderParams = localStorage.getItem('orderParams');
      if (storedOrderParams) {
        const orderParams = JSON.parse(storedOrderParams);
        const {
          note,
          expectedShipDate,
          shortestArrivalDate,
          arrivalDate,
          arrivalTimeZone,
          orderClItemsAttributes,
        } = orderParams.order;

        setNote(note);
        setExpectedShipDate(expectedShipDate);
        setShortestArrivalDate(shortestArrivalDate);
        setArrivalDate(arrivalDate);
        setArrivalTimeZone(arrivalTimeZone);
        setLenses(orderClItemsAttributes);

        const cl_key = Object.keys(orderClItemsAttributes)[0];
        setProductId(orderClItemsAttributes[cl_key].productId);
      }

      const storedFacilityName = localStorage.getItem("facilityName");
      if(storedFacilityName) {
        setFacility((prevFacility: Facility | undefined) => {
          if (prevFacility) {
            return { ...prevFacility, name: JSON.parse(storedFacilityName) };
          }
          return prevFacility;
        });
      }

      const storedFacilityCode = localStorage.getItem("facilityCode");
      if(storedFacilityCode) {
        setFacility((prevFacility: Facility | undefined) => {
          if (prevFacility) {
            return { ...prevFacility, facilityCode: JSON.parse(storedFacilityCode) };
          }
          return prevFacility;
        });
      }

      const storedFacilityId = localStorage.getItem("facilityId");
      if(storedFacilityId) {
        setFacility((prevFacility: Facility | undefined) => {
          if (prevFacility) {
            return { ...prevFacility, id: JSON.parse(storedFacilityId) };
          }
          return prevFacility;
        });
      }

      const storedProductName = localStorage.getItem("productName");
      if(storedProductName) {
        setProductName(JSON.parse(storedProductName));
      }
    } else {
      clearLocalStorage();
    }
  }, []);

  useEffect(() => {
    if (products.length > 0) {
      if(productId !== undefined && productId !== 0) {
        const selectedProduct = products.find((product: LensProduct) => product.id === productId);

        if (selectedProduct) {
          setProductName(selectedProduct.name);
          const isProductAstigma = selectedProduct.lensType === "astigma";
          if(isAstigma !== isProductAstigma) {
            setIsAstigma(isProductAstigma);
            if(!isProductAstigma) {
              setLenses((prevLenses: TrialOrderClItems) =>
                Object.fromEntries(
                  Object.entries(prevLenses).map(([id, lens]) => {
                    return [id, { ...lens, cyl: '' }];
                  }),
                ),
              );
            }
          }

          if (Object.keys(lenses).length > 0) {
            const { stockouts } = selectedProduct;

            setLenses((prevLenses: TrialOrderClItems) =>
              Object.fromEntries(
                Object.entries(prevLenses).map(([id, lens]) => {
                  const checkRange = (start: string, end: string, tp: string) => {
                    var tStart = parseFloat(start);
                    var tEnd = parseFloat(end);
                    var arrayTp = [];
                    for (tEnd; tEnd <= tStart; tEnd+=0.50){arrayTp.push(tEnd)}
                    arrayTp = arrayTp.map((num) => num.toFixed(2));
                    return arrayTp.includes(tp);
                  }
                  const stockout = stockouts.find((stockout: Stockout)  => {
                    return  stockout.fc === lens.fc &&
                            checkRange(stockout.tpStart, stockout.tpEnd, lens.tp ?? '') &&
                            stockout.cyl === lens.cyl &&
                            stockout.dia === lens.dia
                  })

                  if(stockout) {
                    return [id, { ...lens, productId, stockoutMessage: stockout.stockoutMessage }]
                  } else {
                    const { stockoutMessage, ...updatedLensWithoutStockoutMessage } = lens;
                    return [id, { ...updatedLensWithoutStockoutMessage, productId }]
                  }
                }),
              ),
            );

            const updateLensesWithSpecialOrders = async () => {
              const entries = await Promise.all(
                Object.entries(lenses).map(async ([id, lens]) => {
                  if (lens.fc !== "" && lens.fc !== undefined && lens.tp !== "" && lens.tp !== undefined && lens.dia !== "" && lens.dia !== undefined) {
                    try {
                      const response = await setLensDataInfo({
                        specialOrder: {
                          productId: productId,
                          fc: lens.fc,
                          tp: lens.tp,
                          dia: lens.dia,
                          cyl: lens.cyl === "" ? undefined : lens.cyl
                        },
                        lensDataInfo: {
                          facilityId: facility?.id,
                          expectedShipDate: initialExpectedShipDate
                        }
                      });
          
                      if (response.data.isSpecialOrder) {
                        const data = {
                          arrivalDates: response.data.arrivalDates,
                          expectedShipDate: response.data.expectedShipDate,
                          shortestArrivalDate: response.data.shortestArrivalDate,
                        };
          
                        checkSpecialOrder(data, id);
                        return [id, {
                          ...lens,
                          arrivalDates: data.arrivalDates,
                          expectedShipDate: data.expectedShipDate,
                          shortestArrivalDate: data.shortestArrivalDate,
                          productId
                        }];
                      } else {
                        const { arrivalDates, expectedShipDate, shortestArrivalDate, ...updatedLensWithoutSpecialOrder } = lens;
                        checkSpecialOrder(undefined, id);
                        return [id, { ...updatedLensWithoutSpecialOrder, productId }];

                      }
                    } catch (error) {
                      console.error("Error fetching special Order data:", error);
                    }
                  }
                  return [id, lens];
                })
              );
              setLenses(Object.fromEntries(entries));
            };
          
            if (Object.keys(lenses).length > 0 && productId) {
              updateLensesWithSpecialOrders();
            }
          }
        }
      } else {
        setExpectedShipDate(initialExpectedShipDate);
        setArrivalDates(initialArrivalDates);
        setShortestArrivalDate(initialShortestArrivalDate);
        if (Object.keys(lenses).length > 0) {
          setLenses((prevLenses: TrialOrderClItems) =>
            Object.fromEntries(
              Object.entries(prevLenses).map(([id, lens]) => {
                const { stockoutMessage, arrivalDates, expectedShipDate, shortestArrivalDate, ...lensWithoutOption } = lens;
                return [id, { ...lensWithoutOption, productId: undefined }]
              }),
            ),
          );
        }
      }
    }
  }, [productId, products]);

  const checkSpecialOrder = async (data: {expectedShipDate: string, arrivalDates: string[], shortestArrivalDate: string} | undefined, idx: string) => {
    let expectedDates = [
      {
        expectedShipDate: data ? data.expectedShipDate : initialExpectedShipDate,
        arrivalDates: data ? data.arrivalDates : initialArrivalDates,
        shortestArrivalDate: data ? data.shortestArrivalDate : initialShortestArrivalDate
      },
    ]

    Object.entries(lenses as TrialOrderClItems).map(([id, lens]) => {
      if(id !== idx && lens.expectedShipDate && lens.arrivalDates && lens.shortestArrivalDate){
        expectedDates.push(
          {
            expectedShipDate: lens.expectedShipDate,
            arrivalDates: lens.arrivalDates,
            shortestArrivalDate: lens.shortestArrivalDate
          },
        )
      }
    })

    expectedDates.sort((a, b) => {
      return new Date(b.expectedShipDate).getTime() - new Date(a.expectedShipDate).getTime();
    });

    setExpectedShipDate(expectedDates[0].expectedShipDate);
    setArrivalDates(expectedDates[0].arrivalDates);
    setShortestArrivalDate(expectedDates[0].shortestArrivalDate);
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const params: TrialLensOrderParams = {
      order: {
        order_type: 'trial',
        facilityId: facility?.id,
        note: note,
        arrivalDate: arrivalDate,
        arrivalTimeZone: arrivalTimeZone,
        expectedShipDate: expectedShipDate,
        shortestArrivalDate: shortestArrivalDate,
        orderClItemsAttributes: lenses
      },
    }

    try {
      await confirmTrialLens(params);
      setAlertMessageOpen(false);

      localStorage.setItem("orderParams", JSON.stringify(params));
      localStorage.setItem("facilityId", JSON.stringify(facility?.id));
      localStorage.setItem("facilityCode", JSON.stringify(facility?.facilityCode));
      localStorage.setItem("facilityName", JSON.stringify(facility?.name));
      localStorage.setItem("productName", JSON.stringify(productName));

      navigate("/facility/orders/trial_lens/confirm");
      window.scrollTo(0, 0);
    } catch (err: any) {
      setAlertMessageOpen(true);
      if (err.response && err.response.data && err.response.data.errors) {
        setAlertMessageOpen(true);
        setAlertMessages(err.response.data.errors.fullMessages);
        window.scrollTo(0, 0);
      } else {
        setAlertMessageOpen(false);
        console.log('An unexpected error occurred:', err);
      }
    }
  }

  const isStockout = () => {
    return (Object.values(lenses) as TrialOrderClItem[]).some(lens => lens.stockoutMessage !== undefined);
  }

  const handleProductChange = (newValue: string | undefined) => {
    const newProductId = newValue ? parseInt(newValue, 10) : undefined;
    setProductId(newProductId);
    if (newProductId) {
      if (Object.keys(lenses).length === 0) {
        setLenses({
          1: {
            productId: newProductId,
            fc: '',
            tp: '',
            cyl: '',
            dia: '',
            qty: 1,
            eyeType: 'trial'
          }
        });
      } else {
        const updatedLenses = Object.fromEntries(
          Object.entries(lenses).map(([key, lens]) => [
            key,
            { ...lens, productId: newProductId }
          ])
        );
        setLenses(updatedLenses);
      }
    }
  }

  return (
    <React.Fragment>
      <Layout>
        <main className="wrapper" id="main-header">
          <section className="sec-contact">
            <div className="inner">
              <div className="sec-contact__item">
                <h1 className="c-ttl__01">トライアルレンズ注文</h1>
                <AlertMessages open={alertMessageOpen} messages={alertMessages} />
                <div className="contact-box">
                  <div className="contact-box__left">
                    <form onSubmit={handleSubmit}>
                      <div className="contact-box__form mbsp-50 js-item" id="id-01">
                        <h2 className="c-ttl__02">お客様情報</h2>
                        <InfoDisplay label="コードNo" value={facility?.facilityCode} />
                        <InfoDisplay label="得意先名" value={facility?.name} />
                      </div>
                      <div className="contact-box__form mbsp-29 js-item" id="id-02">
                        <h2 className="c-ttl__02">注文内容</h2>
                        <TrialLensDataForm
                          options={getProductOptions(products)}
                          value={productId ? productId.toString() : ""}
                          placeholder="選択してください"
                          onChangeFunctions={[handleProductChange]}
                          size="select-custom--full"
                          isAstigma={isAstigma}
                          lenses={lenses}
                          setLenses={setLenses}
                          productId={productId}
                          products={products}
                          setArrivalDates={setArrivalDates}
                          initialArrivalDates={initialArrivalDates}
                          setExpectedShipDate={setExpectedShipDate}
                          initialExpectedShipDate={initialExpectedShipDate}
                          setShortestArrivalDate={setShortestArrivalDate}
                          initialShortestArrivalDate={initialShortestArrivalDate}
                          facility={facility}
                        />
                        <InputFieldBox label="備考" value={note} onChange={e => setNote(e.target.value)} />
                        <SelectInputBox
                          label="着日指定"
                          required={true}
                          options={arrivalDates.map(date => ({ value: date, label: date }))}
                          value={arrivalDate}
                          placeholder="最短お届け日"
                          onChangeFunctions={[(newValue) => setArrivalDate(newValue || '')]}
                        />
                        <SelectInputBox
                          label="ご希望時間帯"
                          required={true}
                          options={Object.entries(arrivalTimeZoneLabels || {}).map(([key, label]) => ({
                            value: toUnderscoreCase(key),
                            label
                          }))}
                          value={arrivalTimeZone}
                          onChangeFunctions={[(newValue) => setArrivalTimeZone(newValue || "")]}
                        />
                      </div>
                      <button type="submit" className="c-btn__01"disabled={isStockout()}>注文確認</button>
                    </form>
                  </div>
                  <SidebarNavigation items={[{ id: "id-01", label: "お客様情報" }, { id: "id-02", label: "注文内容" }]} />
                </div>
              </div>
            </div>
          </section>
        </main>
      </Layout>
    </React.Fragment>
  )
};

export default TrialLens;